import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import SummaryCard from "components/core/blocks/SummaryCard";
import Upload from "components/core/blocks/Upload";
import { useFetch } from "hooks/api";
import useQueryParams from "hooks/useQueryParams";
import { useMemo, useRef, useState } from "react";
import { Col, Row, Nav } from "react-bootstrap";
import { Link, useHistory, useParams } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import BulkActionsTable from "./merchandise/tables/BulkActionsTable";
import { buildDateColumn } from "./merchandise/columns/buildDateColumn";
import { buildTextColumn } from "./merchandise/columns/buildTextColumn";
import SelectField from "components/core/basic/SelectField";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { buildMetric } from "./merchandise/metrics";
import Accordion from "components/core/basic/Accordion";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { components } from "react-select";
import { formatDatamartFilters } from "utils/formatV2DatamartFilters";
import api from "utils/api";
import { DatesProvider, useDates } from "dates/DatesContext";
import { useAcosOrRoas } from "utils/acosOrRoas";
import { TargetBadges, useTargetReport } from "./SearchTermReport";
import KeywordCell from "components/custom/category/advertisements/KeywordCell";
import TrellisLink from "components/core/basic/TrellisLink";
import { getDashboardTo } from "./AdvertisingOverview";
import { formatTargetText } from "utils/formatTargetText";
import { css } from "../../styled-system/css";
import { SEARCH_TERM_REPORT_MATCH_TYPE_FILTER } from "./advertising/targetLifecycleFilters";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import useAds from "hooks/useAds";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import { Flex } from "../../styled-system/jsx";
import useTargetBulkActions from "hooks/useTargetBulkActions";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import AddTargetsWidget from "components/custom/category/advertisements/AddTargetsWidget";
import { GOALS } from "components/core/blocks/AdPlan/constants";
import { useModuleEnabled } from "redux/selectors/modules";
import { PageContainer } from "components/core/basic/PageContainer";
import Loading from "components/core/blocks/Loading";
import { Button } from "components/core/basic/Button";

const useSQPR = (report) => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { data: rows, isLoading: isSQPReportDataLoading } = useFetch(
    ["sqpreportrows", report?.id],
    `/sqpreportrow/`,
    { report: report?.id },
    { enabled: !!report?.id, select: (data) => data?.data?.results },
  );

  const searchTermReportFilters = useMemo(() => {
    if (!report) {
      return;
    }
    const startDate = report.start_date;
    const endDate = report.end_date;

    const filters = formatDatamartFilters({
      marketplace: marketPlace,
      date__gte: startDate,
      date__lte: endDate,
      sqp_report_id: report?.id,
    });
    return filters;
  }, [marketPlace, report]);

  const { data: searchTermReport, isLoading } = useFetch(
    ["searchTermReport", searchTermReportFilters, marketPlace],
    "/api/data_report_v2/searchtermreport/stats/",
    { dimensions: "query", filters: searchTermReportFilters },
    {
      select: (res) => {
        return res.data;
      },
      enabled: !!searchTermReportFilters,
    },
  );
  const results = useMemo(() => {
    if (!rows) {
      return [];
    }
    const strMap =
      searchTermReport?.reduce((acc, row) => {
        acc[row.query] = row;
        return acc;
      }, {}) ?? {};
    return rows.map((row) => {
      // divide share columns by 100
      row = Object.entries(row).reduce((acc, [key, value]) => {
        if (key.endsWith("_share")) {
          acc[key] = value / 100;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});
      const adsData = strMap?.[row.search_query] ?? {};
      const ctr_total = row.clicks_total / row.impressions_total;
      const ctr_brand_or_asin = row.clicks_brand_or_asin / row.impressions_brand_or_asin;
      const cv_total = row.purchases_total / row.clicks_total;
      const cv_brand_or_asin = row.purchases_brand_or_asin / row.clicks_brand_or_asin;
      const cv_difference = cv_brand_or_asin - cv_total;
      const ctr_difference = ctr_brand_or_asin - ctr_total;
      const ads_impression_share = adsData.impression_share ?? 0;
      const ads_click_share = adsData.click_share ?? 0;
      return {
        ...row,
        ...adsData,
        ctr_brand_or_asin,
        ctr_total,
        cv_brand_or_asin,
        cv_total,
        cv_difference,
        ctr_difference,
        ads_impression_share,
        ads_click_share,
      };
    });
  }, [searchTermReport, rows]);
  return {
    data: results,
    isLoading: isLoading || isSQPReportDataLoading,
  };
};

const ReportsTable = ({ reports, marketPlace }) => {
  const tableRef = useRef();
  const history = useHistory();
  const [selectedReports, setSelectedReports] = useState([]);
  const columnSpecs = [
    buildTextColumn("brand_or_asin", "Report Type"),
    buildTextColumn("reporting_range", "Report Range"),
    buildDateColumn("start_date", "Start Date"),
    buildDateColumn("end_date", "End Date"),
  ];
  const queryClient = useQueryClient();
  const hasDebugModule = useModuleEnabled("DEBUG");

  const deleteReport = useMutation({
    mutationFn: async (ids) => {
      await Promise.all(ids.map(async (id) => api.remove(`/api/sqpreport/${id}/`)));
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sqpreport"],
      });
    },
  });

  return (
    <SummaryCard
      title={"Search Query Performance Reports"}
      status={
        hasDebugModule ?
          <Button
            variant={"link"}
            className={css({ paddingStart: "large", fontSize: "xl" })}
            onPress={() => {
              history.push("/user/report/sqpr_report");
            }}
          >
            View Generated Reports
          </Button>
        : null
      }
    >
      <Col xs={12}>
        <BulkActionsTable
          noDataText={`Upload a Search Query Performance report to get started.`}
          marketPlace={marketPlace}
          tableRef={tableRef}
          tableId={`sqprTable`}
          titleCol={{
            id: "name",
            isStatic: true,
            Header: "Name",
            accessor: "name",
            style: { width: "150px" },
            width: "150px",
            Cell: ({ row }) => {
              return <Link to={`/user/searchqueryperformance/${row._original.id}`}>{row._original.upload.name}</Link>;
            },
            filterMethod: () => {
              return;
            },
            Filter: () => null,
          }}
          columnSpecs={columnSpecs}
          data={reports}
          hideFilterToggleIcon={true}
          defaultSorted={[{ id: "name", desc: false }]}
          getSearchCriteria={(row) => {
            return `${row.upload.name} ${row.reporting_range}`;
          }}
          minRows={5}
          setSelected={setSelectedReports}
          selected={selectedReports}
          isSelectable={true}
          isExpandable={false}
          selectionKey={"id"}
          bulkActionOptions={[
            {
              label: "Delete Report",
              value: "delete",
              action: () => {
                deleteReport.mutate(selectedReports);
              },
              confirmationMessage: (
                <p>
                  Are you sure you want to delete the {selectedReports?.length > 1 ? selectedReports?.length : ""}{" "}
                  selected report{selectedReports?.length > 1 ? "s" : ""}?
                </p>
              ),
            },
          ]}
        />
      </Col>
      <Col xs={12}>
        <Upload
          uploadType="sqp_report"
          fileType="text/csv"
          details={{
            marketplace: marketPlace,
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["sqpreport"],
            });
          }}
        />
      </Col>
    </SummaryCard>
  );
};

export const SearchQueryRowExpander = ({ row, selectedReport, selectedCompareReport }) => {
  const [tab, setTab] = useQueryParams("etab", "keyword");
  const [selectedTargets, setSelectedTargets] = useState([]);

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const hasAddTargetsModule = useModuleEnabled("ADD_TARGETS_REPORTS");

  const { group } = useDates();

  const tableRef = useRef();
  const TABLE_ID = "sqpr-expander-table";
  const filterProps = useCustomTableFilters(marketPlace.marketPlace, TABLE_ID);

  const acosOrRoas = useAcosOrRoas();

  const { data: targetData, isLoading: isTargetDataLoading } = useTargetReport(
    row?.adv_targets?.filter((k) => k) ?? [],
    "target",
    selectedReport.start_date,
    selectedReport.end_date,
    selectedCompareReport?.start_date,
    selectedCompareReport?.end_date,
    group,
  );

  const { data: keywordData, isLoading: isKeywordDataLoading } = useTargetReport(
    row?.adv_keywords?.filter((k) => k) ?? [],
    "keyword",
    selectedReport.start_date,
    selectedReport.end_date,
    selectedCompareReport?.start_date,
    selectedCompareReport?.end_date,
    group,
  );

  const { ads: advertisementData, isLoading: isAdReportDataLoading } = useAds();

  const adsMapping = useMemo(() => {
    return (advertisementData ?? []).reduce((acc, row) => {
      acc[row.ad_id.toString()] = row;
      return acc;
    }, {});
  }, [advertisementData]);

  const tableData = useMemo(() => {
    const data = tab === "target" ? targetData : keywordData;
    return (data?.keywords ?? []).map((row) => {
      const ad = adsMapping[row.advertisement_id];
      return {
        ...row,
        ad_name: ad?.ad_name,
        managed: ad?.managed?.toLowerCase() === "yes",
      };
    });
  }, [keywordData, targetData, adsMapping, tab]);

  const unmanagedAdIds = useMemo(() => {
    return tableData?.filter((ad) => !ad.managed)?.map((ad) => ad.id);
  }, [tableData]);

  const ntmAdIds = useMemo(() => {
    return advertisementData?.filter((ad) => ad.ad_goal === GOALS.IMPORTED_ADS.value)?.map((ad) => ad.id);
  }, [advertisementData]);

  const columnSpecs = useMemo(() => {
    const perfCol = acosOrRoas({ key: "acos", compareKey: "acos_delta" }, { key: "roas", compareKey: "roas_delta" });

    return [
      {
        key: "ad_group_name",
        compareKey: null,
        options: {
          metric: {
            id: "ad_group_name",
            name: "Ad Group Name",
            format: "text",
          },
          hideFilter: true,
          overflowCell: true,
          additionalStyles: { paddingTop: "20px" },
          formatter: (value) => value,
        },
      },
      {
        key: "target_bid",
        compareKey: null,
        options: {
          editCell: false,
          minValue: 0,
          columnTitle: "Bid",
          className: "text-end",
          overrideKey: "manual_bid",
          strikeThroughText: (row) => {
            if (!row?._original?.state) {
              return "";
            }
            if (row._original?.state?.toLowerCase()?.indexOf("negative") > -1) {
              return "text-decoration-line-through";
            }
            return "";
          },
        },
      },
      {
        key: "target_source",
        compareKey: null,
        options: {
          hideFilter: true,
          formatter: (value) =>
            value === "GV_DISCOVERY" ? "initial"
            : value === "harvested" ? "harvesting"
            : snakeCaseToSentenceCase(value)?.toLowerCase(),
        },
      },
      {
        key: "creation_date",
        compareKey: null,
        options: {
          metric: {
            id: "creation_date",
            format: "text",
            accessor: (d) => d?.creation_date,
            name: "Created On",
          },
          formatter: (v) => v ?? "",
          hideFilter: true,
          checked: false,
        },
      },
      {
        key: "match_type",
        compareKey: null,
        options: {
          width: 150,
          hideFilter: true,
          formatter: (v) => {
            return snakeCaseToSentenceCase(v)?.toLowerCase().indexOf("negative") > -1 ?
                snakeCaseToSentenceCase(v)?.toLowerCase()?.replace("negative", "negative ")
              : snakeCaseToSentenceCase(v)?.toLowerCase();
          },
        },
      },
      {
        key: "target_state",
        compareKey: null,
        options: {
          checked: false,
          metric: {
            id: "target_state",
            format: "text",
            accessor: (d) => d?.target_state,
            name: "State",
            options: { checked: true },
          },
          formatter: (v) => snakeCaseToSentenceCase(v)?.toLowerCase() ?? "",
          filterType: "select",
          hideFilter: true,
        },
      },

      { key: "impressions", compareKey: "impressions_percent" },
      { key: "clicks", compareKey: "clicks_percent" },
      { key: "orders", compareKey: "conversion_percent" },
      { key: "ad_spend", compareKey: "ad_spend_percent" },
      { key: "ad_sales", compareKey: "ad_sales_percent" },
      perfCol,
      { key: "cpc", compareKey: "cpc_delta" },
      { key: "conversion_rate", compareKey: "conversion_rate_delta" },
      { key: "ctr", compareKey: "ctr_delta" },
    ];
  }, [acosOrRoas]);

  const { bulkActionOptions } = useTargetBulkActions(
    selectedTargets,
    setSelectedTargets,
    tab,
    tab === "target" ? targetData : keywordData,
  );
  const prefilledTargets = useMemo(() => {
    const targets =
      tab === "target" ?
        [
          {
            text: row.search_query,
            matchTypes: [],
          },
        ]
      : [{ text: row.search_query, matchTypes: ["exact", "phrase", "broad"] }];

    return { targets };
  }, [tab, row]);

  const isLoading = isTargetDataLoading || isKeywordDataLoading || isAdReportDataLoading;

  return (
    <Row className="pt-3">
      {hasAddTargetsModule ?
        <Flex justifyContent={"end"} alignItems={"center"}>
          <AddTargetsWidget
            initialData={prefilledTargets}
            buttonText={`Add ${toTitleCase(tab)}s to Media Plan`}
            targetType={tab}
            renderAdSelector={true}
            ntmAdIds={ntmAdIds}
            onReset={() => {
              setSelectedTargets([]);
            }}
            descriptionText={
              'Add query text to media plans by selecting one below and customizing match types. Once submitted, you can add the query text to more media plans by selecting another and clicking the "Add Keywords" button.'
            }
          />
        </Flex>
      : null}
      <Col xs={12} className="ph-5 mb-5 border-bottom fs-standard">
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
        >
          <Nav.Item>
            <Nav.Link eventKey="keyword">Keywords</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="target">Targets</Nav.Link>
          </Nav.Item>
        </Nav>
      </Col>
      <Col xs={12}>
        <div className={css({ paddingLeft: "large", paddingRight: "large" })}>
          {tableData?.length || isLoading ?
            <BulkActionsTable
              marketPlace={marketPlace}
              tableRef={tableRef}
              tableId={"sqpr-expander"}
              isExpandable={false}
              isStatsLoading={isLoading}
              isSelectable={true}
              selectionKey={"target_id"}
              selected={selectedTargets}
              setSelected={setSelectedTargets}
              bulkActionOptions={bulkActionOptions}
              minRows={0}
              disableSelectionList={unmanagedAdIds}
              titleCol={{
                id: "target",
                isStatic: true,
                Header: "Target",
                accessor: "text",
                format: "text",
                style: { width: "300px" },
                width: 300,
                Cell: (props) => {
                  return (
                    <>
                      <KeywordCell
                        value={formatTargetText(props?.original?.text, {
                          ...props.original?.addtl_data,
                          marketPlace: marketPlace,
                        })}
                        columnWidth={300}
                      />
                      <br />
                      <TrellisLink
                        to={getDashboardTo({
                          type: "advertisement",
                          id: props?.original?.advertisement_id,
                        })}
                        target={"_blank"}
                        style={{
                          textDecoration: "none",
                          fontWeight: "500",
                          fontSize: "1.5rem",
                          cursor: "pointer",
                          paddingRight: "1rem",
                        }}
                      >
                        <small>{props?.original?.ad_name}</small>
                      </TrellisLink>
                      <TargetBadges target={props?.original} />
                    </>
                  );
                },
                Filter: () => null,
              }}
              data={tableData}
              columnSpecs={columnSpecs}
              getSearchCriteria={(row) => {
                const formattedTargetText = formatTargetText(row.text, {
                  ...row.addtl_data,
                  marketPlace: marketPlace,
                });
                const { query, ad_name, ad_group_name } = row ?? {};
                return `${query} ${ad_name} ${ad_group_name} ${formattedTargetText}`;
              }}
              tableRowDescription={"Search Terms"}
              defaultSorted={[{ id: "impressions", desc: true }]}
              isLoading={isLoading}
              filterWidget={true}
              hideFilterToggleIcon={true}
              hideResetFiltersIcon={false}
              excludedFilterColumns={["match_type"]}
              {...filterProps}
              additionalFilterOptions={[SEARCH_TERM_REPORT_MATCH_TYPE_FILTER]}
              handleBulkChanges={() => {}}
            />
          : <Flex
              justifyContent={"center"}
              alignItems={"center"}
              className={css({
                padding: "large",
              })}
            >
              <p> There are no advertised {tab}s associated with this search query for the report's date range.</p>
            </Flex>
          }
        </div>
      </Col>
    </Row>
  );
};

const Dashboard = ({
  marketPlace,
  rows,
  compareRows,
  reports,
  onChange,
  compareReportId,
  selectedReport,
  selectedCompareReport,
  isLoading,
}) => {
  const [total, data, missingQueries] = useMemo(() => {
    if (!rows) {
      return [{}, [], []];
    }

    const summeraize = (data) => {
      const total = data.reduce((acc, row) => {
        return Object.entries(row).reduce((acc, [key, value]) => {
          if (key === "search_query") {
            return acc;
          }
          if (typeof value === "number") {
            acc[key] = (acc[key] || 0) + value;
          }
          return acc;
        }, acc);
      }, {});
      total.search_query_score = total.search_query_score / data.length;
      total.impressions_brand_or_asin_share = total.impressions_brand_or_asin / total.impressions_total;
      total.clicks_total_rate = total.clicks_total / total.impressions_total;
      total.cart_adds_total_rate = total.cart_adds_total / total.clicks_total;
      total.purchases_total_rate = total.purchases_total / total.cart_adds_total;
      total.clicks_brand_or_asin_share = total.clicks_brand_or_asin / total.clicks_total;
      total.cart_adds_brand_or_asin_share = total.cart_adds_brand_or_asin / total.cart_adds_total;
      total.purchases_brand_or_asin_share = total.purchases_brand_or_asin / total.purchases_total;
      total.ctr_total = total.clicks_total / total.impressions_total;
      total.cv_total = total.purchases_total / total.clicks_total;
      total.ctr_brand_or_asin = total.clicks_brand_or_asin / total.impressions_brand_or_asin;
      total.cv_brand_or_asin = total.purchases_brand_or_asin / total.clicks_brand_or_asin;
      total.cv_difference = total.cv_total - total.cv_brand_or_asin;
      total.ctr_difference = total.ctr_total - total.ctr_brand_or_asin;
      return total;
    };

    const compare = (metric, row, compareRow) => {
      const val = row[metric];
      const compareVal = compareRow?.[metric];
      if (compareVal === undefined) {
        return { [metric]: val };
      }
      const delta = val - compareVal;
      const percent = compareVal ? delta / compareVal : 0;
      return {
        [metric]: val,
        [`${metric}_prev`]: compareVal,
        [`${metric}_delta`]: delta,
        [`${metric}_percent`]: percent,
      };
    };

    let total = rows ? summeraize(rows) : {};
    const compareTotal = compareRows ? summeraize(compareRows) : {};

    total = Object.entries(total).reduce((acc, [key]) => {
      if (["search_query", "marketplace"].includes(key)) {
        return acc;
      }
      return {
        ...acc,
        ...compare(key, total, compareTotal),
      };
    });
    if (!compareRows) {
      return [total, rows, []];
    }
    const compareMap = compareRows.reduce((acc, row) => {
      acc[row.search_query] = row;
      return acc;
    }, {});
    const missingCompareQueries = new Set(Object.keys(compareMap));

    const data = rows.map((row) => {
      const compareRow = compareMap[row.search_query];
      if (compareRow) {
        missingCompareQueries.delete(row.search_query);
      }
      return Object.entries(row).reduce((acc, [key]) => {
        if (["search_query", "marketplace"].includes(key)) {
          return { ...acc, [key]: row[key] };
        }
        return {
          ...acc,
          ...compare(key, row, compareRow),
        };
      });
    });
    return [total, data, Array.from(missingCompareQueries)];
  }, [rows, compareRows]);

  const tableRef = useRef();
  const columnSpecs = [
    {
      key: "search_query_score",
      compareKey: "search_query_score_prev",
    },
    {
      key: "search_query_volume",
      compareKey: "search_query_volume_percent",
    },
    {
      key: "impressions_total",
      compareKey: "impressions_total_percent",
    },
    {
      key: "impressions_brand_or_asin",
      compareKey: "impressions_brand_or_asin_percent",
    },
    {
      key: "impressions_brand_or_asin_share",
      compareKey: "impressions_brand_or_asin_share_delta",
    },
    { key: "clicks_total", compareKey: "clicks_total_percent" },
    { key: "clicks_total_rate", compareKey: "clicks_total_rate_delta" },
    { key: "clicks_brand_or_asin", compareKey: "clicks_brand_or_asin_percent" },
    { key: "clicks_brand_or_asin_share", compareKey: "clicks_brand_or_asin_share_delta" },
    { key: "clicks_price_median", compareKey: "clicks_price_median_percent" },
    { key: "clicks_brand_or_asin_price_median", compareKey: "clicks_brand_or_asin_price_median_percent" },
    { key: "clicks_same_day_shipping_speed", compareKey: "clicks_same_day_shipping_speed_percent" },
    { key: "clicks_1d_shipping_speed", compareKey: "clicks_1d_shipping_speed_percent" },
    { key: "clicks_2d_shipping_speed", compareKey: "clicks_2d_shipping_speed_percent" },
    { key: "ctr_total", compareKey: "ctr_total_delta" },
    { key: "ctr_brand_or_asin", compareKey: "ctr_brand_or_asin_delta" },
    { key: "cv_total", compareKey: "cv_total_delta" },
    { key: "cv_brand_or_asin", compareKey: "cv_brand_or_asin_delta" },
    { key: "cv_difference" },
    { key: "ctr_difference" },
    { key: "cart_adds_total", compareKey: "cart_adds_total_percent" },
    { key: "cart_adds_total_rate", compareKey: "cart_adds_total_rate_delta" },
    { key: "cart_adds_brand_or_asin", compareKey: "cart_adds_brand_or_asin_percent" },
    { key: "cart_adds_brand_or_asin_share", compareKey: "cart_adds_brand_or_asin_share_delta" },
    { key: "cart_adds_price_median", compareKey: "cart_adds_price_median_percent" },
    { key: "cart_adds_brand_or_asin_price_median", compareKey: "cart_adds_brand_or_asin_price_median_percent" },
    { key: "cart_adds_same_day_shipping_speed", compareKey: "cart_adds_same_day_shipping_speed_percent" },
    { key: "cart_adds_1d_shipping_speed", compareKey: "cart_adds_1d_shipping_speed_percent" },
    { key: "cart_adds_2d_shipping_speed", compareKey: "cart_adds_2d_shipping_speed_percent" },
    { key: "purchases_total", compareKey: "purchases_total_percent" },
    { key: "purchases_total_rate", compareKey: "purchases_total_rate_delta" },
    { key: "purchases_brand_or_asin", compareKey: "purchases_brand_or_asin_percent" },
    { key: "purchases_brand_or_asin_share", compareKey: "purchases_brand_or_asin_share_percent" },
    { key: "purchases_price_median", compareKey: "purchases_price_median_percent" },
    { key: "purchases_brand_or_asin_price_median", compareKey: "purchases_brand_or_asin_price_median_percent" },
    { key: "purchases_same_day_shipping_speed", compareKey: "purchases_same_day_shipping_speed_percent" },
    { key: "purchases_1d_shipping_speed", compareKey: "purchases_1d_shipping_speed_percent" },
    { key: "purchases_2d_shipping_speed", compareKey: "purchases_2d_shipping_speed_percent" },
    { key: "ad_spend", compareKey: "ad_spend_percent" },
    { key: "ad_sales", compareKey: "ad_sales_percent" },
    { key: "acos", compareKey: "acos_delta" },
    { key: "roas", compareKey: "roas_delta" },
    { key: "clicks", compareKey: "clicks_percent" },
    { key: "impressions", compareKey: "impressions_percent" },
  ];

  columnSpecs.forEach((spec, i) => {
    spec.options = { checked: i < 6 };
    if (!compareReportId) {
      spec.compareKey = undefined;
    }
  });

  const metrics = columnSpecs.map((spec) => buildMetric(marketPlace, { total }, spec.key, spec.compareKey));
  return (
    <MetricsCard
      mobileStyle
      id="sqpr"
      initialMetrics={columnSpecs.map((spec) => spec.key).slice(0, 5)}
      metrics={metrics}
      title="Search Query Performance Reports"
      data={{ total, date: [] }}
      context="search_query_performance"
      showMetricsSelector={true}
      byline={
        selectedReport ?
          `${selectedReport?.brand_or_asin} - ${selectedReport?.reporting_range} (${selectedReport?.start_date} - ${selectedReport?.end_date})`
        : ""
      }
      hideChart
      loading={isLoading}
    >
      <Row>
        <Col xs={12} md={6} lg={9}></Col>
        <Col xs={12} md={6} lg={3}>
          <SelectField
            components={{
              Option: ({ children, ...props }) => (
                <components.Option {...props}>
                  {children}
                  <br />
                  {props.data.details && <small className="text-muted">{props.data.details.upload.name}</small>}
                </components.Option>
              ),
            }}
            label={"Compare"}
            options={reports.map((r) => ({
              value: r.id,
              label: `${r?.brand_or_asin} - ${r?.reporting_range} (${r?.start_date} - ${r?.end_date})`,
              details: r,
            }))}
            value={compareReportId ? parseInt(compareReportId) : null}
            onChange={onChange}
            isClearable
          />
        </Col>
        <Col xs={12}>
          {compareRows && missingQueries.length > 0 && (
            <Accordion title="Warnings">
              <div className="alert alert-warning">
                <strong>Warning:</strong> The following search queries were not found in the report:{" "}
              </div>
              <div>{missingQueries.join(", ")}</div>
            </Accordion>
          )}
        </Col>
        <Col xs={12}>
          <BulkActionsTable
            marketPlace={marketPlace}
            tableRef={tableRef}
            tableId={`sqprrowsTable`}
            titleCol={{
              id: "search_query",
              isStatic: true,
              Header: "Search Query",
              accessor: "search_query",
              style: { width: "150px" },
              width: "150px",
              Cell: ({ row }) => {
                return row.search_query;
              },
              filterMethod: () => {
                return;
              },
              Filter: () => null,
            }}
            columnSpecs={columnSpecs}
            data={data}
            hideFilterToggleIcon={true}
            isSelectable={false}
            isExpandable={true}
            selectionKey={"search_query"}
            defaultSorted={[{ id: "search_query", desc: false }]}
            getSearchCriteria={(row) => {
              return `${row.search_query}`;
            }}
            isLoading={isLoading}
            minRows={5}
            ExpandedContent={({ row }) => {
              return (
                <ExpanderContainer>
                  <SearchQueryRowExpander
                    row={row.original}
                    query={row.original.search_query}
                    marketPlace={marketPlace}
                    selectedReport={selectedReport}
                    selectedCompareReport={selectedCompareReport}
                  />
                </ExpanderContainer>
              );
            }}
          />
        </Col>
      </Row>
    </MetricsCard>
  );
};

const SearchQueryPerformance = () => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { id: selectedReportId = null } = useParams();
  const [compareReportId, setCompareReportId] = useQueryParams("compare", null);
  const { data: reports, isLoading: isReportsDataLoading } = useFetch(
    ["sqpreport", marketPlace],
    "/sqpreport/",
    { marketplace: marketPlace },
    { select: (data) => data?.data?.results },
  );
  // Start fetching ads metadata ASAP to cache since this can be the bottleneck for rendering the table expander
  useAds();

  const breadcrumbs = [{ name: "Search Query Performance", href: "/user/searchqueryperformance" }];

  const selectedReport = useMemo(
    () => reports?.find((r) => r.id.toString() === selectedReportId),
    [reports, selectedReportId],
  );
  const { data: rows, isLoading: isSQPReportDataLoading } = useSQPR(selectedReport);

  const selectedCompareReport = useMemo(
    () => reports?.find((r) => r.id.toString() === compareReportId),
    [reports, compareReportId],
  );
  const { data: compareRows, isLoading: isSQPReportCompareDataLoading } = useSQPR(selectedCompareReport);

  if (selectedReport) {
    breadcrumbs.push({
      name:
        selectedReport ?
          `
      ${selectedReport?.brand_or_asin} - ${selectedReport?.reporting_range} (${selectedReport?.start_date} - ${selectedReport?.end_date})
      `
        : "",
      href: `/user/searchqueryperformance/${selectedReportId}`,
    });
  }

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadcrumbs} hideDateSelector />
      <PageContainer withBreadcrumb>
        <div className="fs-standard">
          {selectedReport ?
            <Dashboard
              reports={reports}
              rows={rows}
              compareRows={compareRows}
              compareReportId={compareReportId}
              selectedCompareReport={selectedCompareReport}
              onChange={setCompareReportId}
              selectedReport={selectedReport}
              isLoading={isSQPReportDataLoading || isSQPReportCompareDataLoading}
            />
          : isReportsDataLoading ?
            <Loading />
          : <ReportsTable reports={reports} marketPlace={marketPlace} />}
        </div>
      </PageContainer>
    </DatesProvider>
  );
};

export default SearchQueryPerformance;
